// What each tag control offers, from the one `archived=in_use` read (spec 09
// §4.1, D-323 ruling 5). Pure, so the book's filter, the bulk bar's two
// pickers and the 360's add control cannot disagree about which tags they
// offer.
//
//   * The Tag FILTER offers every active tag and every archived tag an active
//     customer still carries (`in_use`), because an archived tag still
//     filters and a reader looking for its customers must be able to pick it.
//   * ADDING offers active tags only: the attach door refuses an archived tag
//     409 `TAG_ARCHIVED`, so offering one is offering a refusal.
//   * The bulk bar's REMOVE offers every tag the selected rows carry, archived
//     ones included, because untagging an archived tag is allowed and is how a
//     curator clears one off the book.

import type { CustomerTagRef, SalesTag } from "../../hooks/crm-queries";

/** Tags the book's Tag filter offers, active first, each part in the wire's
 * order. */
export function filterTagOptions(tags: readonly SalesTag[]): readonly SalesTag[] {
  const active = tags.filter((tag) => tag.status === "active");
  const archived = tags.filter((tag) => tag.status === "archived" && tag.in_use);
  return [...active, ...archived];
}

/** Tags an add control offers: active ones not already on the customer. */
export function addableTags(
  tags: readonly SalesTag[],
  carried: readonly { readonly id: string }[] = [],
): readonly SalesTag[] {
  const on = new Set(carried.map((tag) => tag.id));
  return tags.filter((tag) => tag.status === "active" && !on.has(tag.id));
}

/** Tags the selected rows carry, each once, in name order: what the bulk
 * bar's Remove tag offers. */
export function carriedTags(
  rows: readonly { readonly tags: readonly CustomerTagRef[] }[],
): readonly CustomerTagRef[] {
  const byId = new Map<string, CustomerTagRef>();
  for (const row of rows) for (const tag of row.tags) if (!byId.has(tag.id)) byId.set(tag.id, tag);
  return [...byId.values()].sort((a, b) => {
    const left = a.name.toLowerCase();
    const right = b.name.toLowerCase();
    if (left !== right) return left < right ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}
